/**
 * The review list for the `proposal:classification` output kind on the
 * generic agent report page: every category and altitude this agent proposed
 * that waits for a person, with accept, accept only the category or only the
 * altitude, and reject. Each decision goes through the same store functions
 * the item page uses, so the triggers that enforce reviewers and stamp
 * decision records fire the same way.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, Loader2, Sparkles, X } from 'lucide-react';
import { Button } from '../../ui/Button';
import { Callout } from '../../ds';
import { useItemClassificationStore } from '../../../stores/prioritization/itemClassificationStore';
import { useUserStore } from '../../../stores/prioritization/userStore';
import { when, type OutputReviewProps } from './types';
import { useOpenClassificationProposals, useOpenClassificationStore } from './useOpenClassificationProposals';

export function ClassificationProposalsReview({ def, workspaceId, mayReview, onChanged, titleOf }: OutputReviewProps) {
  const userId = useUserStore((s) => s.userId);
  const accept = useItemClassificationStore((s) => s.accept);
  const partialAccept = useItemClassificationStore((s) => s.partialAccept);
  const reject = useItemClassificationStore((s) => s.reject);
  const reload = useOpenClassificationStore((s) => s.load);
  const open = useOpenClassificationProposals(def.agentType);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const run = async (id: string, act: () => Promise<{ success: boolean; error?: string }>) => {
    setBusyId(id);
    setError(null);
    const result = await act();
    if (!result.success) setError(result.error ?? 'Not saved');
    await reload(workspaceId, def.agentType);
    onChanged();
    setBusyId(null);
  };

  return (
    <div className="space-y-2">
      {error && <Callout tone="coral">{error}</Callout>}
      {!mayReview && open.length > 0 && (
        <Callout tone="neutral">Reviewing what {def.name} proposes needs one of its review roles. A workspace administrator can grant one.</Callout>
      )}
      {open.length === 0 ? (
        <div className="card text-center py-10">
          <Sparkles className="h-6 w-6 mx-auto text-[var(--text-ter)]" />
          <p className="text-sm font-medium text-[var(--text-pri)] mt-2">Nothing waiting</p>
          <p className="text-xs text-[var(--text-ter)] mt-1">Run {def.name}, or ask it about one item from the item's page.</p>
        </div>
      ) : open.map((p) => {
        const f = p.classificationFields;
        const busy = busyId === p.id;
        return (
          <div key={p.id} className="card space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <Link to={`/prioritization/items/${p.itemId}`} className="text-sm font-medium text-[var(--text-pri)] underline">{titleOf(p.itemId)}</Link>
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-[var(--slate-dim)] text-[var(--text-pri)] border border-[var(--slate)] capitalize">
                <Sparkles className="h-3 w-3" /> {f.category} · altitude {f.altitudeLevel}
              </span>
              {f.executionProfile && <span className="text-xs text-[var(--text-ter)]">{f.executionProfile}</span>}
              {f.confidence && <span className="text-xs text-[var(--text-ter)]">{f.confidence} confidence</span>}
              <span className="ml-auto text-xs text-[var(--text-ter)]">{when(p.createdAt)}</span>
            </div>
            {f.rationale && <p className="text-xs text-[var(--text-ter)] italic">"{f.rationale}"</p>}
            {mayReview && userId && (
              <div className="flex flex-wrap justify-end gap-2">
                <Button variant="ghost" size="sm" disabled={busy} onClick={() => void run(p.id, () => reject({ proposalId: p.id, itemId: p.itemId }))}>
                  <X className="h-3.5 w-3.5" /> Reject
                </Button>
                <Button variant="secondary" size="sm" disabled={busy}
                  onClick={() => void run(p.id, () => partialAccept({ proposalId: p.id, itemId: p.itemId, userId, acceptCategory: true, acceptAltitude: false }))}>
                  Category only
                </Button>
                <Button variant="secondary" size="sm" disabled={busy}
                  onClick={() => void run(p.id, () => partialAccept({ proposalId: p.id, itemId: p.itemId, userId, acceptCategory: false, acceptAltitude: true }))}>
                  Altitude only
                </Button>
                <Button variant="primary" size="sm" disabled={busy} onClick={() => void run(p.id, () => accept({ proposalId: p.id, itemId: p.itemId, userId }))}>
                  {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />} Accept
                </Button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
